import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Components and Molecules
import Button from "../../../components/Desktop/Button";

// Images
import homeHeroA from "../../../assets/img/HomePage/Desktop/home_hero_a.png";
import homeHeroB from "../../../assets/img/HomePage/Desktop/home_hero_b.png";

const HomeHero = () => {
  const navigate = useNavigate();

  return (
    <section className="px-[120px] py-[100px] mt-[90px]">
      <div className="max-w-[1550px] mx-auto">
        <div className="text-center mb-[48px]">
          <h2 className="text-[48px] font-raleway font-bold text-primaryDark-0 leading-tight mb-[24px]">
            <span className="text-primaryDark-400">
              Connecting Food Businesses
            </span>
            <br />
            With Culinary Resources
          </h2>

          <p className="text-[16px] text-[#4D4D4D] font-open-sans mb-[24px] leading-loose max-w-[700px] mx-auto">
            HomeCook's intuitive platform helps food entrepreneurs access
            culinary resources, while also empowering resource providers to
            diversify their revenue streams.
          </p>

          {/* Buttons */}
          <div className="flex items-center justify-center gap-[16px]">
            <Button
              label="I’m a Food Entrepreneur"
              variant="primary"
              onClick={() => {
                navigate("/food-entrepreneurs");
              }}
            />
            <Button
              label="I’m a Resource Provider"
              variant="secondary"
              onClick={() => {
                navigate("/resource-providers");
              }}
            />
          </div>
        </div>

        {/* Carousel */}
        <HomeCarousel />
      </div>
    </section>
  );
};

const HomeCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const delay = 3000;

  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  // Auto-slide effect
  useEffect(() => {
    if (isHovered) return;

    resetTimeout();

    timeoutRef.current = setTimeout(() => {
      setActiveIndex((prev) => (prev === 0 ? 1 : 0));
    }, delay);

    return () => resetTimeout();
  }, [activeIndex, isHovered]);

  const resetTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const prevSlide = () => setActiveIndex(activeIndex === 0 ? 1 : 0);
  const nextSlide = () => setActiveIndex(activeIndex === 1 ? 0 : 1);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    setIsHovered(true); // pause auto slide
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    setIsHovered(false);

    handleSwipe();
  };

  const handleSwipe = () => {
    const start = touchStartX.current;
    const end = touchEndX.current;
    if (start === null || end === null) return;
    const distance = start - end;

    if (distance > 50) {
      nextSlide(); // swipe left
    }

    if (distance < -50) {
      prevSlide(); // swipe right
    }
  };

  return (
    <div
      className="p-[30px] shadow-xl shadow-text-0/24 border border-text-0/24 rounded-[16px] transition-all"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onMouseEnter={() => {
        setIsHovered(true);
        resetTimeout();
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        resetTimeout();
      }}
    >
      <div className="relative w-full overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {/* Card 1 */}
          <HomeCarouselCard
            card_title="Food Entrepreneurs get access to commercial Kitchens"
            card_variant="food_entrepreneurs"
            card_image={homeHeroA}
            onPrevSlide={prevSlide}
            onNextSlide={nextSlide}
          />

          {/* Card 2 */}
          <HomeCarouselCard
            card_title="Providers Monetize their Culinary Resources"
            card_variant="resource_providers"
            card_image={homeHeroB}
            onPrevSlide={prevSlide}
            onNextSlide={nextSlide}
          />
        </div>
      </div>
    </div>
  );
};

type HomeCarouselCardProps = {
  card_title: string;
  card_variant: "food_entrepreneurs" | "resource_providers";
  card_image: string;
  onPrevSlide: () => void;
  onNextSlide: () => void;
};

const HomeCarouselCard: React.FC<HomeCarouselCardProps> = ({
  card_title,
  card_variant,
  card_image,
  onPrevSlide,
  onNextSlide,
}) => {
  if (card_variant === "food_entrepreneurs") {
    return (
      <div className="w-full flex-shrink-0">
        {/* Header */}
        <div className="border border-text-0/24 px-[24px] py-[16px] mb-[8px] flex items-center justify-between w-full">
          <p className="text-[18px] font-semibold font-open-sans text-text-0">
            {card_title}
          </p>

          {/* Buttons */}
          <div className="flex items-center justify-center gap-[8px] bg-primaryDark-400 w-[64px] h-[40px] rounded-[40px]">
            <button
              type="button"
              className="cursor-pointer w-[16px] h-[8px] rounded-[24px] bg-text-1000"
              onClick={onPrevSlide}
            />
            <button
              type="button"
              className="cursor-pointer w-[8px] h-[8px] rounded-[24px] bg-[#7DC5D8]"
              onClick={onNextSlide}
            />
          </div>
        </div>

        {/* Image */}
        <div className="border border-text-0/24 w-full">
          <img
            src={card_image}
            alt="Card"
            className="w-[1120px] h-auto object-cover mx-auto"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex-shrink-0">
      {/* Header */}
      <div className="border border-text-0/24 px-[24px] py-[16px] mb-[8px] flex items-center justify-between w-full">
        <p className="text-[18px] font-semibold font-open-sans text-text-0">
          {card_title}
        </p>

        {/* Buttons */}
        <div className="flex items-center justify-center gap-[8px] bg-primaryDark-400 w-[64px] h-[40px] rounded-[40px]">
          <button
            type="button"
            className="cursor-pointer w-[8px] h-[8px] rounded-[24px] bg-[#7DC5D8]"
            onClick={onPrevSlide}
          />
          <button
            type="button"
            className="cursor-pointer w-[16px] h-[8px] rounded-[24px] bg-text-1000"
            onClick={onNextSlide}
          />
        </div>
      </div>

      {/* Image */}
      <div className="border border-text-0/24 w-full">
        <img
          src={card_image}
          alt="Card"
          className="w-[1120px] h-auto object-cover mx-auto"
        />
      </div>
    </div>
  );
};

export default HomeHero;
